// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import { AutocompleteProductsStyles } from "@insite/content-library/Widgets/Header/AutocompleteProducts";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Link from "@store/MobiusOverrides/Link/Link";
import { autocompleteProps } from "@store/Themes/common/props/autocomplete";
import useIsMobile from "@store/Framework/Common/Hooks/useIsMobile";
import BunzlAutoCompleteProduct, {
    BunzlAutoCompleteProductStyles,
} from "@store/Library/Components/Bunzl/AutoComplete/BunzlAutoCompleteProduct";
import { linkStyles } from "@store/Themes/common/mixins/link";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { Fragment, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";

interface AutoCompleteProductsProps {
    products: ProductAutocompleteItemModel[];
    goToUrl: (url: string) => void;
    focusedItem?: AutocompleteItemModel;
    extendedStyles?: AutocompleteProductsStyles;
    isAuthenticated: boolean;
    searchRef?: React.RefObject<HTMLInputElement>;
    doSearch: () => void;
    variant?: "outlined" | "elevated";
    handleAutoCompleteSearch: () => void;
}

interface AutoCompleteProductsStyles {
    wrapper?: InjectableCss;
    title?: InjectableCss;
    erpNumberText?: InjectableCss;
    headerText?: InjectableCss;
    autoCompleteProductStyles?: BunzlAutoCompleteProductStyles;
    gridWrapper?: InjectableCss;
    moreResultsLink?: InjectableCss;
    linkWrapperStyles?: InjectableCss;
}

const autoCompleteProductsStyles: AutoCompleteProductsStyles = {
    autoCompleteProductStyles: {},
    linkWrapperStyles: {
        css: css`
            display: flex;
            width: 100%;
            align-items: center;
            justify-content: center;
        `,
    },
    moreResultsLink: {
        css: css`
            ${linkStyles("primary.main", "secondary.main", true)}
        `,
    },
    gridWrapper: {
        css: css`
            display: grid;
            @media (min-width: ${pxToRem(1500)}) {
                grid-template-columns: repeat(auto-fill, minmax(calc(33% - 1rem), 1fr));
            }
            grid-template-columns: repeat(auto-fill, minmax(calc(50% - 1rem), 1fr));
            width: 100%;
            gap: ${pxToRem(16)};
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            gap: ${pxToRem(16)};
        `,
    },
    title: autocompleteProps.title,
    erpNumberText: autocompleteProps.erpNumber,

    headerText: {
        css: css`
            display: block;
            margin: ${pxToRem(18, 0, 5, 0)};
            padding-bottom: ${pxToRem(5)};
            text-align: left;
            font-weight: ${x => x.theme.design.typography.weight.bold};
            border-bottom: 1px solid ${getColor("common.border")};
            &:first-child {
                margin-top: 0;
            }
        `,
    },
};
const BunzlAutoCompleteProducts: React.FC<AutoCompleteProductsProps> = ({
    products,
    handleAutoCompleteSearch,
    goToUrl,
    extendedStyles,
    isAuthenticated,
    doSearch,
    variant,
    focusedItem,
}) => {
    const dispatch = useAppDispatch();
    const isMobile = useIsMobile();
    const prevIds = useRef<string | null>(null);
    const styles = useMergeProps(autoCompleteProductsStyles, extendedStyles);
    const productModels = useSelector((state: ApplicationState) =>
        getProductsForProductInfoList(state, "autoCompleteProducts"),
    );
    const productInfosById = useSelector(
        (state: ApplicationState) => state.components.productInfoLists.productInfoListById["autoCompleteProducts"],
    )?.productInfoByProductId;

    useEffect(() => {
        const ids = productModels?.map(product => product.id);
        if (!isAuthenticated || isMobile) {
            return;
        }
        if (!products.every(p => ids?.includes(p?.id ?? ""))) {
            const idString = ids?.join(",");
            if (prevIds.current !== idString) {
                dispatch(
                    loadProductInfoList({
                        id: "autoCompleteProducts",
                        getProductCollectionParameter: { productIds: products?.map(p => p.id ?? "") ?? [] },
                    }),
                );
                if (idString) {
                    prevIds.current = idString;
                }
            }
        }
    }, [dispatch, productModels, products]);

    const handleMoreResultsClick = () => {
        doSearch();
        handleAutoCompleteSearch();
    };
    if (isAuthenticated && !productModels) {
        return null;
    }
    return (
        <StyledWrapper {...styles.wrapper} tabIndex={-1} data-test-selector="autoComplete_products">
            <Typography
                {...styles.headerText}
                data-test-selector="autoComplete_products_header"
                className="autocomplete-products-header"
            >
                {translate("Products")}
            </Typography>

            <StyledWrapper {...styles.gridWrapper} data-test-selector="autoComplete_products_grid">
                {products.map(product => {
                    const productId = product?.id ?? "";
                    const productInfo =
                        productInfosById && productId in productInfosById
                            ? productInfosById[productId as keyof typeof productInfosById]
                            : null;
                    const productModel = productModels?.find(model => model.id === product.id);
                    const productContext: ProductContextModel | null =
                        productModel && productInfo ? { product: productModel, productInfo } : null;

                    const isFocused = focusedItem === product;
                    return (
                        <Fragment key={product.title}>
                            {productContext ? (
                                <ProductContext.Provider value={productContext}>
                                    <BunzlAutoCompleteProduct
                                        isFocused={isFocused}
                                        variant={variant}
                                        autocompleteProductItem={product}
                                        extendedStyles={styles.autoCompleteProductStyles}
                                        isAuthenticated={isAuthenticated}
                                        goToUrl={goToUrl}
                                        data-test-selector={`autoComplete_products_item_${productId}`}
                                    />
                                </ProductContext.Provider>
                            ) : (
                                <BunzlAutoCompleteProduct
                                    goToUrl={goToUrl}
                                    isFocused={isFocused}
                                    variant={variant}
                                    autocompleteProductItem={product}
                                    isAuthenticated={isAuthenticated}
                                    extendedStyles={styles.autoCompleteProductStyles}
                                    data-test-selector={`autoComplete_products_item_${productId}`}
                                />
                            )}
                        </Fragment>
                    );
                })}
            </StyledWrapper>
            <StyledWrapper {...styles.linkWrapperStyles} data-test-selector="autoComplete_products_moreResultsWrapper">
                <Link
                    {...styles.moreResultsLink}
                    onClick={handleMoreResultsClick}
                    data-test-selector="autoComplete_products_moreResults"
                >
                    {translate("Show More Results")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default BunzlAutoCompleteProducts;
